/** @prettier */

import {createContext, useCallback, useEffect, useMemo, useState} from 'react';
import {Grid, Stack, Container, Pagination, Box} from '@mui/material';
import {useLocation, useParams} from 'react-router-dom';

//MTH component import
import SiteSubContents from './SiteSubContents';
import {AirconGraph} from '../../component/graph/component/AirconGraph';
import AirconOperation from '../../component/AirconOperation';
import MainSiteDetail from './MainSiteDetail';
import SiteDetail from './SiteDetail';
import MainSiteSubHeader from './MainSiteSubHeader';
import GroupDetail from './SLGroupDetail';
import QtonDetail from './QtonDetail';
import ChillerDetail from './ChillerDetail';
import RTGraph from '../../component/graph/component/RTGraph';
import HCCVQDetail from './HCCVQDetail';
import {ModbusOUGraph} from '../../component/graph/component/ModbusGraph';
import SiteInfoBox, {AddSiteBox} from './MysiteSiteBox';
import HCCVQGroupDetail from './HCCVQGroupDetail';
import Footer from '../MainFooter';
//API import
import {getCompositionsSites} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {
  GraphFormType,
  GraphTargetType,
  layoutUnitListType,
  filterSettingProps,
  mysite,
  mysiteProps,
} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import {useRunCountAllUnit} from '../../hooks/states/useRunCountAllUnit';
import {useUserInfo} from './MainMySite';
import {useCulcPowAllUnit} from '../../hooks/states/useCulcPowAllUnit';

export type ContextApplyFilterTabType = {
  /** 適用するフィルタ */
  applyFilter: {
    /** フロア数 */
    floor: string;
    /** グループID */
    groupID: string;
    /** 熱源機の機種 */
    otherUnitType: mysite.filterProps['unitType'];
  };
  /** 表示種別 */
  applyTab: mysite.tabProps['type'];
};
/** フィルタの適用用context */
export const ContextApplyFilterTab = createContext({
  applyFilter: (filter: ContextApplyFilterTabType['applyFilter']) => {},
  applyTab: (tab: ContextApplyFilterTabType['applyTab']) => {},
});

/**
 * サイト一覧
 * @param props props
 * - siteInfoList 画面に表示するサイトの情報
 * - siteNumInfoList 画面に表示するサイトの機器毎の稼働台数
 * - sitePowerInfoList 画面に表示するサイトの機器毎の消費電力
 * - selectedSiteInfo 選択されているサイトの情報
 * - setSelectedSiteInfo 選択されているサイトの情報のset関数
 * - totalPage 全体のページ数
 * - SiteNum サイト登録数
 * - selectedPage 選択したページのset関数
 * - setSelectedPage 選択したページのset関数
 * @returns JSX
 */
const SiteList = (props: {
  siteInfoList: apiIF.resParameters.siteInformations[];
  siteNumInfoList: mysite.siteData[];
  sitePowerInfoList: mysiteProps.siteKWData[];
  selectedSiteInfo: apiIF.resParameters.siteInformations | undefined;
  setSelectedSiteInfo: React.Dispatch<
    React.SetStateAction<apiIF.resParameters.siteInformations | undefined>
  >;
  totalPage: number;
  siteNum: number;
  selectedPage: number;
  setSelectedPage: React.Dispatch<React.SetStateAction<number>>;
}) => {
  /**
   * ページ数変更時
   * @param event
   * @param value
   */
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    props.setSelectedPage(value);
  };

  return (
    <Stack className="siteList">
      <Box className="siteList__list">
        <Grid container columnSpacing={3} rowSpacing={4}>
          {props.siteInfoList.map(siteInfo => {
            /** サイトが選択されているかどうか */
            const selected =
              props.selectedSiteInfo !== undefined &&
              siteInfo['site-id'] === props.selectedSiteInfo['site-id'];
            /** サイトの機器毎の稼働台数 */
            const siteNumInfo = props.siteNumInfoList.find(info => {
              return info['siteID'] === siteInfo['site-id'];
            });
            /** サイトの機器毎の消費電力 */
            const sitePowerInfo = props.sitePowerInfoList.find(info => {
              return info['site-id'] === siteInfo['site-id'];
            });

            return (
              <Grid
                item
                xs="auto"
                key={`MysiteSiteBoxKey${siteInfo['site-id']}`}>
                <SiteInfoBox
                  isSelected={selected}
                  siteInfoData={siteInfo}
                  setViewSubContentsData={props.setSelectedSiteInfo}
                  viewSiteData={siteNumInfo}
                  viewSitePwData={sitePowerInfo}
                />
              </Grid>
            );
          })}
          {/** サイト追加ボタンは表示されているページの一番後ろに配置 */}
          <Grid item xs="auto">
            <AddSiteBox siteNum={props.siteNum} />
          </Grid>
        </Grid>
      </Box>
      <Box className="siteList__pagination">
        <Pagination
          count={props.totalPage}
          onChange={handlePageChange}
          page={props.selectedPage}
          showFirstButton
          showLastButton
        />
      </Box>
    </Stack>
  );
};

const MySiteArea = () => {
  //リロード時サブコンテンツの情報をローカルストレージから読み出す
  //非表示のときはundefinedをセットする
  let initialSubContentsDisplay = undefined;
  const storageData = localStorage.getItem('viewSubContentData');
  if (storageData !== null) {
    initialSubContentsDisplay = JSON.parse(storageData);
  }

  //リロード時サブコンテンツに表示していたサイトのページ情報をローカルストレージから読み出す
  /** 選択ページ初期値 */
  let initialSelectedPage = 1;
  /** 総ページ数初期値 */
  let initialTotalPage = 1;
  const pageData = localStorage.getItem('selectedPage');
  if (pageData !== null) {
    let parsePageData = JSON.parse(pageData);
    initialSelectedPage = parsePageData['item-page'];
    initialTotalPage = parsePageData['total-page'];
  }

  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {target} = useParams();
  //ユーザ情報取得
  const {userInfo} = useUserInfo();

  //prettier-ignore
  //画面に表示しているサイトの情報
  const [siteProfile, setSiteProfile] = useState<apiIF.resParameters.siteInformations[] | null>(null);
  //prettier-ignore
  //フッターテキスト
  const [footerText, setFooterText] = useState<apiIF.res.getSettingUser['footer-text']>('');
  //選択したページ
  const [selectedPage, setSelectedPage] = useState<number>(initialSelectedPage);
  //全体のページ数
  const [totalPage, setTotalPage] = useState<number>(initialTotalPage);
  //サイト登録数
  const [siteNum, setSiteNum] = useState<number>(0);
  //prettier-ignore
  //並び替え方法
  const [displayOrder, setDisplayOrder] = useState<filterSettingProps.filterSettingData['displayOrder']>('registeredDate');
  //prettier-ignore
  //1ページあたりの表示数
  const [displayedInPage, setDisplayedInPage] = useState<filterSettingProps.filterSettingData['displayedInPage']>('10');
  //prettier-ignore
  //機器フィルター
  const [deviceFilter, setDeviceFilter] = useState<filterSettingProps.filterSettingData['deviceFilter']>('unfiltered');
  //prettier-ignore
  //権限フィルター
  const [authorityFilter, setAuthorityFilter] = useState<filterSettingProps.filterSettingData['authorityFilter']>('unfiltered');
  /** フィルター設定 */
  const filterSetting: filterSettingProps.filterSettingData = {
    displayOrder: displayOrder,
    displayedInPage: displayedInPage,
    deviceFilter: deviceFilter,
    authorityFilter: authorityFilter,
    setDisplayOrder: setDisplayOrder,
    setDisplayedInPage: setDisplayedInPage,
    setDeviceFilter: setDeviceFilter,
    setAuthorityFilter: setAuthorityFilter,
  };
  //prettier-ignore
  //サブコンテンツに表示しているサイトの情報
  const [viewSubContentsData, setViewSubContentsData] = useState<apiIF.resParameters.siteInformations | undefined>(initialSubContentsDisplay);
  //サブコンテンツで選択している項目（フロア or グループ or 空調機）
  const [selectedItem, setSelectedItem] = useState('');
  //SL通信の室内機、インターフェースのID、型式リスト
  const [unitTypeList, setUnitTypeList] = useState<layoutUnitListType>([]);
  //prettier-ignore
  //運転指令の指令単位 undefinedの場合は指令画面を非表示
  const [opeTarget, setOpeTarget] = useState<apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined>();
  //運転指令対象のID
  const [opeTargetID, setOpeTargetID] = useState('');
  //運転指令対象のサイトID
  const [opeSiteID, setOpeSiteID] = useState('');
  // /** グラフ描画選択した */
  // const [graphSiteID, setGraphSiteID] = useState('');
  //グラフ画面に表示するタイトル
  const [graphTitle, setGraphTitle] = useState('');
  //グラフに描画する対象
  const [graphTarget, setGraphTarget] = useState<GraphTargetType>('');
  //グラフに描画する対象のID
  const [graphTargetID, setGraphTargetID] = useState('');
  //グラフの種類
  const [graphType, setGraphType] = useState<GraphFormType>('bar');
  //グラフに描画する対象ユニットのグループID
  const [groupID, setGroupID] = useState('');
  //詳細画面のタブ切り替え変数
  const [tabType, setTabType] = useState<
    'diagram' | 'bar' | 'line' | 'barLineMixed'
  >('diagram');
  //サイトに登録されているQton子機の情報
  const [childUnitInfo, setChildUnitInfo] = useState<
    apiIF.resParameters.rtUnitInformations[]
  >([]);

  //サブコンテンツの表示種別
  const [viewType, setViewType] = useState<mysite.tabProps['type']>('floors');
  //フィルタ
  const [floorFilter, setFloorFilter] = useState<string>(''); //''はフィルタ無しの状態
  const [groupFilter, setGroupFilter] = useState<string>(''); //''はフィルタ無しの状態
  const [otherFilter, setOtherFilter] =
    useState<mysite.filterProps['unitType']>(''); //''はフィルタ無しの状態

  //HCCVQのグラフ領域の表示の有無を判断するための変数
  const [isHCCVQGraphShow, setIsHCCVQGraphShow] = useState(false);

  /**
   * フィルタを適用
   * @param filter 適用するフィルタのID
   */
  const applyFilter = (filter: ContextApplyFilterTabType['applyFilter']) => {
    setFloorFilter(filter.floor);
    setGroupFilter(filter.groupID);
    setOtherFilter(filter.otherUnitType);
  };
  /**
   * タブの切り替え
   * @param tab サブコンテンツの表示種別
   */
  const applyTab = (tab: ContextApplyFilterTabType['applyTab']) => {
    setViewType(tab);
  };

  /** サイト台数情報 */
  const viewSiteData = useRunCountAllUnit(siteProfile);
  /** サイト消費電力情報 */
  const viewSitePwData = useCulcPowAllUnit(siteProfile);

  //サイト情報取得
  useEffect(() => {
    if (userInfo === null) return;
    setFooterText(userInfo['footer-text']);

    const query: apiIF.reqParameters.getCompositionsSites = {
      'item-page': selectedPage,
      'sort': displayOrder,
      'max-result': Number(displayedInPage),
      'device-type': deviceFilter,
      'user-authority': authorityFilter,
    };
    getCompositionsSites(query)
      .then(response => {
        setSiteProfile(response['site-informations']);
        setTotalPage(Number(response['total-pages']));
        setSiteNum(Number(response['total-results']));
        if (response['site-informations'].length === 0) {
          // 表示するサイトがなければストレージの情報を削除する
          localStorage.removeItem('viewSubContentData');
          setViewSubContentsData(undefined);
        } else {
          if (
            viewSubContentsData !== undefined &&
            response['site-informations'].some(siteInfo => {
              return siteInfo['site-id'] === viewSubContentsData['site-id'];
            }) === false
          ) {
            //表示していたサイトがページ切替等で取得できなくなった場合は、
            //APIから取得したサイト内、一番最初のサイトを表示する
            setViewSubContentsData(response['site-informations'][0]);
          }
        }
      })
      .catch(error => {
        setSiteProfile(null);
      });
  }, [
    userInfo,
    selectedPage,
    displayOrder,
    displayedInPage,
    deviceFilter,
    authorityFilter,
  ]);

  useEffect(() => {
    // 選択中のページが最大ページを超えている場合は1ページ目に戻す
    if (selectedPage > totalPage) {
      setSelectedPage(1);
    }
  }, [totalPage]);

  //リロード時に情報を復元するため、サブコンテンツの
  //情報（viewSubContentsData,selectedPage）をローカルストレージに保存する
  useEffect(() => {
    if (viewSubContentsData === undefined) {
      //非表示のときは記憶しない（読み出し時はnullとなる）
      localStorage.removeItem('viewSubContentData');
      setViewType('floors');
    } else {
      localStorage.setItem(
        'viewSubContentData',
        JSON.stringify(viewSubContentsData)
      );
      // 現在選択中サイトのページ位置
      localStorage.setItem(
        'selectedPage',
        JSON.stringify({
          'item-page': selectedPage,
          'total-page': totalPage,
        })
      );
    }
  }, [viewSubContentsData]);

  //prettier-ignore
  //指令ボタン押下時の実行関数
  const operationOpen = useCallback((
    target   : apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
    targetID : string,
    siteID   : string
  ) => {
    //指令対象、対象ID、サイトIDをセットする
    setOpeTarget(target);
    setOpeTargetID(targetID);
    setOpeSiteID(siteID);
    //グラフ画面を閉じる
    setGraphTarget('');
  }, []);
  /** 指令画面を閉じる関数 */
  const operationCloseClick = useCallback(() => {
    setOpeTarget(undefined);
  }, []);
  /** グラフに描画するデータをセットする */
  const setGraphParam = useCallback(
    (
      target: GraphTargetType, //グラフの対象(グラフ非表示時は'')
      targetID?: string, //対象のID
      type?: GraphFormType, //グラフの種類
      title?: string, //グラフのタイトルに表示する文字列
      tabName?: 'bar' | 'line' | 'barLineMixed' | 'diagram', //詳細画面のタブ切り替え変数
      childUnitInfo?: apiIF.resParameters.rtUnitInformations[], //子機のユニットIDとユニット名
      groupID?: string
    ) => {
      setGraphTarget(target);
      if (targetID !== undefined) setGraphTargetID(targetID);
      if (type !== undefined) setGraphType(type);
      if (title !== undefined) setGraphTitle(title);
      if (tabName !== undefined) {
        setTabType(tabName);
      } else if (type !== 'bar' && type !== 'line' && type !== 'barLineMixed') {
        setTabType('diagram');
      }
      if (childUnitInfo !== undefined) setChildUnitInfo(childUnitInfo);
      if (groupID !== undefined) setGroupID(groupID);
      //指令画面を閉じる
      setOpeTarget(undefined);
    },
    []
  );

  /** 運転指令に関する変数をpropsで渡しやすいようまとめたオブジェクト */
  const operationProps = {
    opeTarget,
    opeTargetID,
    opeSiteID,
    operationCloseClick,
  };

  //グラフ用のデータを、子コンポーネントに渡すためにオブジェクトにまとめる
  const graphProps = useMemo(
    () => ({
      siteID: viewSubContentsData?.['site-id'],
      graphTitle: graphTitle,
      graphTarget: graphTarget,
      graphTargetID: graphTargetID,
      graphType: graphType,
      childUnitInfo: childUnitInfo,
      groupID: groupID,
      setGraphParam,
    }),
    [graphTitle, graphTarget, graphTargetID, graphType, childUnitInfo]
  );
  return (
    <>
      {/* 空調機グラフ */}
      <AirconGraph graphProps={graphProps} />
      {/* エアコン指令画面 */}
      {opeTarget && <AirconOperation operationProps={operationProps} />}
      <Grid container>
        {/* メインコンテンツ */}
        <Grid item xs>
          <Stack className="mySiteMainContents">
            {/** サブヘッダ */}
            {siteProfile === null ? (
              <Box className="mySiteMainContents__subHeader--siteList"></Box>
            ) : location.pathname.endsWith('my-site') ? (
              <MainSiteSubHeader
                filterSetting={filterSetting}
                setSelectedPage={setSelectedPage}
              />
            ) : (
              <MainSiteDetail
                shouldDisplayWeather={true}
                location={viewSubContentsData?.location}
              />
            )}
            {/** 詳細 */}
            <Stack className="mySiteMainContents__detail">
              <Box className="mySiteMainContents__detail__main">
                {siteProfile === null ? (
                  //サイト追加ボタン
                  <Stack className="siteList">
                    <Box className="siteList__list">
                      <AddSiteBox siteNum={siteNum} />
                    </Box>
                  </Stack>
                ) : location.pathname.endsWith('state') ? (
                  // サイト詳細
                  <ContextApplyFilterTab.Provider
                    value={{applyFilter, applyTab}}>
                    <SiteDetail
                      siteName={viewSubContentsData?.['site-name']}
                      siteAuthority={viewSubContentsData?.['site-authority']}
                      operationOpen={operationOpen}
                      setGraphParam={setGraphParam}
                    />
                  </ContextApplyFilterTab.Provider>
                ) : target === 'group' ? (
                  // グループ詳細
                  <GroupDetail
                    selectedItem={selectedItem}
                    unitTypeList={unitTypeList}
                    setSelectedItem={setSelectedItem}
                  />
                ) : target === 'qton' ? (
                  //Q-ton詳細
                  <Container fixed>
                    <Stack spacing={3}>
                      <QtonDetail
                        setGraphParam={setGraphParam}
                        setTabType={setTabType}
                        tabType={tabType}
                      />
                      {graphTarget === 'qton' ? (
                        <RTGraph graphProps={graphProps} />
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </Container>
                ) : target === 'hyozan' ? (
                  //Hyozan詳細
                  <Container fixed>
                    <Stack spacing={3}>
                      <HCCVQDetail setISPaidGW={setIsHCCVQGraphShow} />
                      {graphTarget === 'hyozan' && isHCCVQGraphShow === true ? (
                        <RTGraph graphProps={graphProps} />
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </Container>
                ) : target === 'modbus' ? (
                  //modbus室外機詳細
                  <Container fixed>
                    <Stack spacing={3}>
                      <HCCVQDetail setISPaidGW={setIsHCCVQGraphShow} />
                      {isHCCVQGraphShow === true ? (
                        <ModbusOUGraph graphProps={graphProps} />
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </Container>
                ) : target === 'modbus-group' ? (
                  userInfo !== null && (
                    //modbusグループ詳細
                    <HCCVQGroupDetail
                      selectedItem={selectedItem}
                      setSelectedItem={setSelectedItem}
                      unitType="Modbus"
                      timezone={
                        viewSubContentsData === undefined ||
                        viewSubContentsData.timezone === undefined
                          ? ''
                          : viewSubContentsData.timezone
                      }
                      //prettier-ignore
                      displaySetting={{
                        autoSwitching: userInfo['modbus-display']['auto-switching'] === 'active',
                        timeFrame: userInfo['modbus-display']['switching-time-frame'],
                        singleFloorPlan: userInfo['modbus-display']['display-item']['single-floor-plan'],
                        multiFloorPlan: userInfo['modbus-display']['display-item']['single-floor-plan'],
                      }}
                      language={userInfo['language']}
                    />
                  )
                ) : target === 'hyozan-group' ? (
                  userInfo !== null && (
                    //Hyozanグループ詳細
                    <HCCVQGroupDetail
                      selectedItem={selectedItem}
                      setSelectedItem={setSelectedItem}
                      unitType="Hyozan"
                      timezone={
                        viewSubContentsData === undefined ||
                        viewSubContentsData.timezone === undefined
                          ? ''
                          : viewSubContentsData.timezone
                      }
                      //prettier-ignore
                      displaySetting={{
                        autoSwitching: userInfo['modbus-display']['auto-switching'] === 'active',
                        timeFrame: userInfo['modbus-display']['switching-time-frame'],
                        singleFloorPlan: userInfo['modbus-display']['display-item']['single-floor-plan'],
                        multiFloorPlan: userInfo['modbus-display']['display-item']['single-floor-plan'],
                      }}
                      language={userInfo['language']}
                    />
                  )
                ) : target === 'chiller' ? (
                  //Chiller詳細
                  <Container fixed>
                    <Stack spacing={3}>
                      <ChillerDetail
                        setGraphParam={setGraphParam}
                        setTabType={setTabType}
                        tabType={tabType}
                      />
                      {graphTarget === 'chiller' ? (
                        <RTGraph graphProps={graphProps} />
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </Container>
                ) : (
                  //サイト一覧
                  <SiteList
                    siteInfoList={siteProfile}
                    siteNumInfoList={viewSiteData}
                    sitePowerInfoList={viewSitePwData}
                    selectedSiteInfo={viewSubContentsData}
                    setSelectedSiteInfo={setViewSubContentsData}
                    totalPage={totalPage}
                    siteNum={siteNum}
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                  />
                )}
              </Box>
              <Box className="mySiteMainContents__detail__footer">
                <Footer footerText={footerText} />
              </Box>
            </Stack>
          </Stack>
        </Grid>
        {/* サブコンテンツ */}
        {siteProfile !== null && viewSubContentsData !== undefined ? (
          <>
            <ContextApplyFilterTab.Provider value={{applyFilter, applyTab}}>
              <SiteSubContents
                siteProfile={viewSubContentsData}
                setSiteProfile={setViewSubContentsData}
                siteNumInfoList={viewSiteData}
                sitePowerInfoList={viewSitePwData}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                setUnitTypeList={setUnitTypeList}
                operationOpen={operationOpen}
                setGraphParam={setGraphParam}
                viewType={viewType}
                floorFilter={floorFilter}
                groupFilter={groupFilter}
                otherFilter={otherFilter}
              />
            </ContextApplyFilterTab.Provider>

            {/** 以下はサブコンテンツを表示したときのメインコンテンツの幅を調整するために配置 */}
            <Grid item xs="auto">
              <Box className="subContents__background"></Box>
            </Grid>
          </>
        ) : (
          <></>
        )}
      </Grid>
    </>
  );
};
export default MySiteArea;
